import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ImageCapture from '../components/ImageCapture';
import GoogleButton from '../components/GoogleButton';
import SignaturePad from '../components/SignaturePad';
import './Profile.css';

function Profile() {
    const { user, fetchUser } = useAuth();
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Signature states
    const [signature, setSignature] = useState(null);
    const [signatureLoading, setSignatureLoading] = useState(false);
    const [uploadingSignature, setUploadingSignature] = useState(false);
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const signatureInputRef = useRef(null);

    // Avatar states
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const avatarInputRef = useRef(null);

    // Email states
    const [emailInput, setEmailInput] = useState('');
    const [emailStatus, setEmailStatus] = useState({ email: '', email_verified: false });
    const [emailEditing, setEmailEditing] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [verifyCode, setVerifyCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const emailInputRef = useRef(null);       // โฟกัสช่องกรอกอีเมลเมื่อถูก direct มาจากที่อื่น (#email)
    const emailFocusedRef = useRef(false);     // กันโฟกัสซ้ำ

    // 2FA states
    const [twofaEnabled, setTwofaEnabled] = useState(false);
    const [twofaSetup, setTwofaSetup] = useState(null); // { secret, formattedKey, otpauthUri, qr }
    const [twofaCode, setTwofaCode] = useState('');
    const [twofaBusy, setTwofaBusy] = useState(false);
    const [showManualKey, setShowManualKey] = useState(false);
    const [keyCopied, setKeyCopied] = useState(false);

    // Phone states
    const [phone, setPhone] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneEditing, setPhoneEditing] = useState(false);
    const [phoneSaving, setPhoneSaving] = useState(false);

    // Position change request states
    const [positionData, setPositionData] = useState({ position_override: '', email_verified: false, request: null });
    const [positionInput, setPositionInput] = useState('');
    const [positionPhone, setPositionPhone] = useState('');
    const [positionEvidence, setPositionEvidence] = useState(null);
    const [positionFormOpen, setPositionFormOpen] = useState(false);
    const [positionSaving, setPositionSaving] = useState(false);

    useEffect(() => {
        fetchSignature();
        fetchEmailStatus();
        fetch2faStatus();
        fetchPhone();
        fetchPositionRequest();
    }, []);

    // ถูกพามาเพราะพยายามพิมพ์/ส่งออกเอกสารทั้งที่ยังไม่ยืนยันอีเมล → แสดงเหตุผล
    useEffect(() => {
        try {
            const m = sessionStorage.getItem('emailGateMsg');
            if (m) {
                setMessage({ type: 'error', text: m });
                sessionStorage.removeItem('emailGateMsg');
            }
        } catch (e) { /* ignore */ }
    }, []);

    // ถูก direct มาจากปุ่ม "เพิ่ม/ยืนยันอีเมล" (hash #email) → เลื่อนไป + โฟกัสช่องอีเมลให้รู้ว่าต้องกรอก
    useEffect(() => {
        if (emailFocusedRef.current) return;
        if (typeof window === 'undefined' || window.location.hash !== '#email') return;
        const card = document.getElementById('profile-email-card');
        if (!card) return;
        emailFocusedRef.current = true;
        const t = setTimeout(() => {
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            card.classList.add('section-highlight');
            setTimeout(() => card.classList.remove('section-highlight'), 2200);
            if (emailInputRef.current) {
                try { emailInputRef.current.focus(); } catch (e) { /* ignore */ }
            }
        }, 300);
        // เคลียร์ hash กัน trigger ซ้ำตอน re-render/refresh
        if (window.history.replaceState) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        return () => clearTimeout(t);
    }, [emailStatus]);

    const fetchPositionRequest = async () => {
        try {
            const res = await api.get('/auth/position-request');
            setPositionData(res.data);
        } catch (err) {
            // เงียบไว้ ถ้า endpoint ยังไม่พร้อม
        }
    };

    const currentPosition = () =>
        positionData.position_override
        || user?.employee?.positionname
        || user?.employee?.pos_name
        || '';

    const handleSubmitPositionRequest = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        const requested = positionInput.trim();
        if (!requested) {
            setMessage({ type: 'error', text: 'กรุณากรอกชื่อตำแหน่งที่ต้องการแก้ไข' });
            return;
        }
        if (!positionEvidence) {
            setMessage({ type: 'error', text: 'กรุณาแนบรูปหลักฐาน (บัตรเจ้าหน้าที่/เอกสารราชการที่ระบุตำแหน่ง)' });
            return;
        }
        const posPhoneDigits = positionPhone.replace(/\D/g, '');
        if (posPhoneDigits.length < 9 || posPhoneDigits.length > 10) {
            setMessage({ type: 'error', text: 'กรุณากรอกเบอร์โทรติดต่อกลับให้ถูกต้อง (9-10 หลัก)' });
            return;
        }
        setPositionSaving(true);
        try {
            const fd = new FormData();
            fd.append('position', requested);
            fd.append('phone', posPhoneDigits);
            fd.append('current_position', currentPosition() || '');
            fd.append('evidence', positionEvidence);
            const res = await api.post('/auth/position-request', fd);
            setMessage({ type: 'success', text: res.data.message || 'ส่งคำขอแล้ว' });
            setPositionFormOpen(false);
            setPositionInput('');
            setPositionPhone('');
            setPositionEvidence(null);
            await fetchPositionRequest();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งคำขอไม่สำเร็จ' });
        } finally {
            setPositionSaving(false);
        }
    };

    const handleCancelPositionRequest = async () => {
        if (!window.confirm('ต้องการยกเลิกคำขอแก้ไขชื่อตำแหน่งหรือไม่?')) return;
        try {
            await api.delete('/auth/position-request');
            setMessage({ type: 'success', text: 'ยกเลิกคำขอแล้ว' });
            await fetchPositionRequest();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // อัปโหลดหลักฐานให้คำขอที่รอดำเนินการแต่ยังไม่มีหลักฐาน (เคสตกค้าง)
    const handleUploadEvidence = async () => {
        if (!positionEvidence) {
            setMessage({ type: 'error', text: 'กรุณาแนบรูปหลักฐานก่อน' });
            return;
        }
        setPositionSaving(true);
        setMessage({ type: '', text: '' });
        try {
            const fd = new FormData();
            fd.append('evidence', positionEvidence);
            const res = await api.post('/auth/position-request/evidence', fd);
            setMessage({ type: 'success', text: res.data.message || 'อัปโหลดหลักฐานแล้ว' });
            setPositionEvidence(null);
            await fetchPositionRequest();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'อัปโหลดไม่สำเร็จ' });
        } finally {
            setPositionSaving(false);
        }
    };

    const fetchPhone = async () => {
        try {
            const res = await api.get('/auth/phone');
            setPhone(res.data.phone || '');
            setPhoneInput(res.data.phone || '');
        } catch (err) {
            // เงียบไว้ ถ้า endpoint ยังไม่พร้อม
        }
    };

    const formatPhone = (value) => {
        const d = (value || '').replace(/\D/g, '');
        if (d.length === 10) return d.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        if (d.length === 9) return d.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
        return d;
    };

    const handleSavePhone = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        const digits = phoneInput.replace(/\D/g, '');
        if (digits.length < 9 || digits.length > 10) {
            setMessage({ type: 'error', text: 'กรุณากรอกเบอร์โทรให้ถูกต้อง (9-10 หลัก)' });
            return;
        }
        setPhoneSaving(true);
        try {
            const res = await api.post('/auth/phone', { phone: digits });
            setPhone(res.data.phone || digits);
            setPhoneEditing(false);
            setMessage({ type: 'success', text: res.data.message || 'บันทึกเบอร์โทรสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'บันทึกเบอร์โทรไม่สำเร็จ' });
        } finally {
            setPhoneSaving(false);
        }
    };

    const handleRemovePhone = async () => {
        if (!window.confirm('ต้องการลบเบอร์โทรออกจากบัญชีหรือไม่?')) return;
        try {
            await api.delete('/auth/phone');
            setPhone('');
            setPhoneInput('');
            setPhoneEditing(false);
            setMessage({ type: 'success', text: 'ลบเบอร์โทรแล้ว' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    const fetch2faStatus = async () => {
        try {
            const res = await api.get('/auth/2fa/status');
            setTwofaEnabled(!!res.data.enabled);
        } catch (err) { /* endpoint อาจยังไม่พร้อม */ }
    };

    const startTwofaSetup = async () => {
        setMessage({ type: '', text: '' });
        setTwofaBusy(true);
        try {
            const res = await api.post('/auth/2fa/setup');
            setTwofaSetup(res.data);
            setShowManualKey(false);
            setTwofaCode('');
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เริ่มตั้งค่า 2FA ไม่สำเร็จ' });
        } finally {
            setTwofaBusy(false);
        }
    };

    const confirmEnable2fa = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(twofaCode.trim())) {
            setMessage({ type: 'error', text: 'กรุณากรอกรหัส 6 หลักจากแอป' });
            return;
        }
        setTwofaBusy(true);
        try {
            await api.post('/auth/2fa/enable', { code: twofaCode.trim() });
            setMessage({ type: 'success', text: 'เปิดใช้งาน 2FA สำเร็จ — ครั้งต่อไปที่เข้าระบบจะต้องกรอกรหัสจากแอป' });
            setTwofaSetup(null);
            setTwofaCode('');
            setTwofaEnabled(true);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ยืนยันไม่สำเร็จ' });
        } finally {
            setTwofaBusy(false);
        }
    };

    const disable2fa = async () => {
        const code = window.prompt('ยืนยันการปิด 2FA — กรอกรหัส 6 หลักจากแอป (หรือเว้นว่างแล้วกดตกลงเพื่อใช้รหัสผ่านแทน):', '');
        if (code === null) return;
        let password = '';
        if (!code.trim()) {
            password = window.prompt('กรอกรหัสผ่านของคุณเพื่อปิด 2FA:', '') || '';
            if (!password) return;
        }
        setTwofaBusy(true);
        try {
            await api.post('/auth/2fa/disable', { code: code.trim(), password });
            setMessage({ type: 'success', text: 'ปิดใช้งาน 2FA แล้ว' });
            setTwofaEnabled(false);
            setTwofaSetup(null);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ปิดไม่สำเร็จ' });
        } finally {
            setTwofaBusy(false);
        }
    };

    const copyKey = async () => {
        if (!twofaSetup?.secret) return;
        try {
            await navigator.clipboard.writeText(twofaSetup.secret);
            setKeyCopied(true);
            setTimeout(() => setKeyCopied(false), 2000);
        } catch (e) {
            // fallback: เลือกข้อความเอง
            setMessage({ type: 'error', text: 'คัดลอกอัตโนมัติไม่ได้ กรุณาเลือกคัดลอกด้วยตนเอง' });
        }
    };

    const fetchEmailStatus = async () => {
        try {
            const res = await api.get('/auth/email');
            setEmailStatus(res.data);
            setEmailInput(res.data.email || '');
        } catch (err) {
            // เงียบไว้ ถ้า endpoint ยังไม่พร้อม
        }
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim())) {
            setMessage({ type: 'error', text: 'รูปแบบอีเมลไม่ถูกต้อง' });
            return;
        }
        setEmailSending(true);
        try {
            const res = await api.post('/auth/email', { email: emailInput.trim() });
            setCodeSent(true);
            setEmailEditing(false);
            setMessage({ type: 'success', text: res.data.message || 'ส่งรหัสยืนยันแล้ว' });
            await fetchEmailStatus();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งรหัสยืนยันไม่สำเร็จ' });
        } finally {
            setEmailSending(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        if (!/^\d{6}$/.test(verifyCode.trim())) {
            setMessage({ type: 'error', text: 'กรุณากรอกรหัสยืนยัน 6 หลัก' });
            return;
        }
        setVerifying(true);
        try {
            await api.post('/auth/email/verify', { code: verifyCode.trim() });
            setMessage({ type: 'success', text: 'ยืนยันอีเมลสำเร็จ' });
            setCodeSent(false);
            setVerifyCode('');
            // อีเมลยืนยันแล้ว → ปลดล็อกช่องแก้ตำแหน่งทันที (sync positionData ด้วย)
            setPositionData(prev => ({ ...prev, email_verified: true }));
            await fetchEmailStatus();
            await fetchUser();
            await fetchPositionRequest();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ยืนยันไม่สำเร็จ' });
        } finally {
            setVerifying(false);
        }
    };

    const handleRemoveEmail = async () => {
        if (!window.confirm('ต้องการลบอีเมลออกจากบัญชีหรือไม่?')) return;
        try {
            await api.delete('/auth/email');
            setEmailStatus({ email: '', email_verified: false });
            setEmailInput('');
            setCodeSent(false);
            setEmailEditing(false);
            // ไม่มีอีเมลที่ยืนยันแล้ว → ล็อกช่องแก้ตำแหน่งกลับ (sync positionData ด้วย)
            setPositionData(prev => ({ ...prev, email_verified: false }));
            setMessage({ type: 'success', text: 'ลบอีเมลแล้ว' });
            await fetchUser();
            await fetchPositionRequest();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // ผูกอีเมลด้วย Google — ได้ ID token แล้วส่งให้ backend ตั้ง email + ยืนยันทันที
    const handleLinkGoogle = useCallback(async (credential) => {
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post('/auth/link-google', { credential });
            setEmailStatus({ email: res.data.email, email_verified: true });
            setEmailInput(res.data.email || '');
            setEmailEditing(false);
            setCodeSent(false);
            // อีเมลยืนยันแล้ว → ปลดล็อกช่องแก้ตำแหน่งทันที (sync positionData ด้วย)
            setPositionData(prev => ({ ...prev, email_verified: true }));
            setMessage({ type: 'success', text: res.data.message || 'ผูกอีเมล Google สำเร็จ' });
            await fetchUser();
            await fetchPositionRequest();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ผูกอีเมล Google ไม่สำเร็จ' });
        }
    }, [fetchUser]);

    const handleAvatarUpload = async (file) => {
        if (!file) return;

        if (file.type && !['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(file.type)) {
            setMessage({ type: 'error', text: 'รองรับเฉพาะไฟล์ PNG, JPEG, GIF, WEBP' });
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'ขนาดไฟล์ต้องไม่เกิน 5MB' });
            return;
        }

        const formData = new FormData();
        formData.append('avatar', file);

        try {
            setUploadingAvatar(true);
            await api.post('/auth/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            await fetchUser();
            setMessage({ type: 'success', text: 'อัพโหลดรูปภาพสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัพโหลด' });
        } finally {
            setUploadingAvatar(false);
            if (avatarInputRef.current) {
                avatarInputRef.current.value = '';
            }
        }
    };

    const handleDeleteAvatar = async () => {
        if (!window.confirm('ต้องการลบรูปภาพโปรไฟล์หรือไม่?')) return;

        try {
            setUploadingAvatar(true);
            await api.delete('/auth/avatar');
            await fetchUser();
            setMessage({ type: 'success', text: 'ลบรูปภาพสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setUploadingAvatar(false);
        }
    };

    const fetchSignature = async () => {
        try {
            setSignatureLoading(true);
            const response = await api.get('/signatures/my-signature');
            setSignature(response.data);
        } catch (err) {
            console.error('Error fetching signature:', err);
        } finally {
            setSignatureLoading(false);
        }
    };

    const handleSignatureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
            setMessage({ type: 'error', text: 'รองรับเฉพาะไฟล์ PNG, JPEG, GIF' });
            return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'ขนาดไฟล์ต้องไม่เกิน 2MB' });
            return;
        }

        const formData = new FormData();
        formData.append('signature', file);

        try {
            setUploadingSignature(true);
            const response = await api.post('/signatures/my-signature', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSignature(response.data.signature);
            setMessage({ type: 'success', text: 'อัพโหลดลายเซ็นสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัพโหลด' });
        } finally {
            setUploadingSignature(false);
            if (signatureInputRef.current) {
                signatureInputRef.current.value = '';
            }
        }
    };

    const handleDeleteSignature = async () => {
        if (!window.confirm('ต้องการลบลายเซ็นนี้หรือไม่?')) return;

        try {
            setUploadingSignature(true);
            await api.delete('/signatures/my-signature');
            setSignature(null);
            setMessage({ type: 'success', text: 'ลบลายเซ็นสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setUploadingSignature(false);
        }
    };

    // บันทึกลายเซ็นที่วาดด้วยนิ้ว (blob PNG โปร่งใส) — อัปโหลดผ่าน endpoint เดิม
    const handleSaveDrawnSignature = async (blob) => {
        const formData = new FormData();
        formData.append('signature', new File([blob], 'signature.png', { type: 'image/png' }));
        try {
            setUploadingSignature(true);
            const response = await api.post('/signatures/my-signature', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSignature(response.data.signature);
            setShowSignaturePad(false);
            setMessage({ type: 'success', text: 'บันทึกลายเซ็นสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'บันทึกลายเซ็นไม่สำเร็จ' });
        } finally {
            setUploadingSignature(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'รหัสผ่านใหม่ไม่ตรงกัน' });
            return;
        }

        if (newPassword.length < 6) {
            setMessage({ type: 'error', text: 'รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร' });
            return;
        }

        setLoading(true);
        try {
            await api.post('/auth/change-password', {
                currentPassword,
                newPassword
            });
            setMessage({ type: 'success', text: 'เปลี่ยนรหัสผ่านสำเร็จ' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordForm(false);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="profile-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/">หน้าหลัก</Link>
                <span>/</span>
                <span>โปรไฟล์</span>
            </div>

            <h1 className="page-title">โปรไฟล์ของฉัน</h1>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* User Info */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ข้อมูลส่วนตัว</div>
                <div className="steam-card-body">
                    <div className="profile-header">
                        <div className="profile-avatar-wrapper">
                            <div className={`profile-avatar ${user?.avatar_url ? 'has-image' : ''}`}>
                                {user?.avatar_url ? (
                                    <img
                                        src={`/api${user.avatar_url}`}
                                        alt={user?.fullname || 'avatar'}
                                    />
                                ) : (
                                    user?.fullname?.charAt(0) || 'U'
                                )}
                                <label className="avatar-edit-overlay" title="เปลี่ยนรูปโปรไฟล์">
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                        <circle cx="12" cy="13" r="4" />
                                    </svg>
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                        onChange={(e) => handleAvatarUpload(e.target.files && e.target.files[0])}
                                        ref={avatarInputRef}
                                        style={{ display: 'none' }}
                                        disabled={uploadingAvatar}
                                    />
                                </label>
                                {uploadingAvatar && (
                                    <div className="avatar-loading">
                                        <div className="spinner-sm"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="profile-info">
                            <h2>{user?.fullname || 'ผู้ใช้งาน'}</h2>
                            <span className="badge badge-primary">{user?.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ผู้ใช้งาน'}</span>
                            <div className="avatar-actions">
                                <ImageCapture
                                    onSelect={handleAvatarUpload}
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    captureLabel="ถ่ายรูป"
                                    browseLabel={user?.avatar_url ? 'เลือกรูปใหม่' : 'เลือกรูป'}
                                />
                                {user?.avatar_url && (
                                    <button
                                        type="button"
                                        className="avatar-action-link danger"
                                        onClick={handleDeleteAvatar}
                                        disabled={uploadingAvatar}
                                    >
                                        ลบรูป
                                    </button>
                                )}
                            </div>
                            <small className="avatar-hint">PNG, JPEG, GIF, WEBP — ไม่เกิน 5MB</small>
                        </div>
                    </div>

                    <div className="profile-details">
                        <div className="detail-item">
                            <span className="label">เลขบัตรประชาชน:</span>
                            <span className="value">{user?.cid?.replace(/(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/, '$1-$2-$3-$4-$5')}</span>
                        </div>
                        {user?.employee && (
                            <>
                                <div className="detail-item">
                                    <span className="label">รหัสพนักงาน:</span>
                                    <span className="value">{user.employee.emp_code || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">ตำแหน่ง:</span>
                                    <span className="value">{positionData.position_override || user.employee.positionname || user.employee.pos_name || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">หน่วยงาน:</span>
                                    <span className="value">{user.employee.depts || user.employee.dept_code || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">เลขที่บัญชี:</span>
                                    <span className="value account-value">
                                        <img
                                            className="ktb-icon"
                                            src={`${process.env.PUBLIC_URL}/ktb-logo.png`}
                                            width="22"
                                            height="22"
                                            alt="ธนาคารกรุงไทย"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                        {user.employee.pre_code}{user.employee.emp_ac_no?.replace(user.employee.pre_code, '')}
                                    </span>
                                </div>
                            </>
                        )}
                        <div className="detail-item">
                            <span className="label">วันที่ลงทะเบียน:</span>
                            <span className="value">
                                {user?.created_at ? new Date(user.created_at).toLocaleDateString('th-TH', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                }) : '-'}
                            </span>
                        </div>
                        <div className="detail-item">
                            <span className="label">เข้าใช้งานล่าสุด:</span>
                            <span className="value">
                                {user?.last_login ? new Date(user.last_login).toLocaleString('th-TH') : '-'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Email / Account for documents */}
            <div className="steam-card mb-3" id="profile-email-card">
                <div className="steam-card-header flex-between">
                    <span>อีเมลสำหรับรับ-ส่งเอกสาร</span>
                    {emailStatus.email && (
                        emailStatus.email_verified ? (
                            <span className="badge badge-success">ยืนยันแล้ว</span>
                        ) : (
                            <span className="badge badge-warning">ยังไม่ยืนยัน</span>
                        )
                    )}
                </div>
                <div className="steam-card-body">
                    <p className="text-muted" style={{ marginBottom: 14, fontSize: 13 }}>
                        เพิ่มอีเมลและยืนยัน เพื่อใช้รับเอกสาร เช่น สลิปเงินเดือน หรือหนังสือรับรอง ในอนาคต
                    </p>

                    {/* แสดงอีเมลปัจจุบัน */}
                    {emailStatus.email && !emailEditing && (
                        <div className="email-current">
                            <div className="email-current-info">
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                                <span className="email-address">{emailStatus.email}</span>
                                {emailStatus.email_verified && (
                                    <svg className="email-verified-check" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                )}
                            </div>
                            <div className="email-current-actions">
                                <button type="button" className="email-link-btn" onClick={() => { setEmailEditing(true); setCodeSent(false); }}>
                                    เปลี่ยนอีเมล
                                </button>
                                <button type="button" className="email-link-btn danger" onClick={handleRemoveEmail}>
                                    ลบ
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ผูกบัญชีด้วย Google (แนะนำ — อยู่บนสุดเหนือช่องกรอกอีเมล, ยืนยันทันทีไม่ต้องรอรหัส) */}
                    {!emailStatus.email_verified && (
                        <div className="email-google-option email-google-top">
                            <div className="login-google">
                                <GoogleButton text="continue_with" onCredential={handleLinkGoogle} />
                            </div>
                            <p className="login-google-note">
                                ผูกด้วยบัญชี Google — ระบบจะตั้งอีเมลและยืนยันให้ทันที (ไม่ต้องรอรหัส) แล้วใช้เข้าสู่ระบบด้วย Google ได้
                            </p>
                            {(!emailStatus.email || emailEditing) && (
                                <div className="login-divider"><span>หรือกรอกอีเมลเอง</span></div>
                            )}
                        </div>
                    )}

                    {/* ฟอร์มกรอก/เปลี่ยนอีเมล */}
                    {(!emailStatus.email || emailEditing) && (
                        <form onSubmit={handleSendCode} className="email-form">
                            <div className="form-group">
                                <label className="form-label">อีเมล</label>
                                <input
                                    ref={emailInputRef}
                                    type="email"
                                    className="steam-input"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    placeholder="เช่น you@example.com"
                                    required
                                />
                            </div>
                            <div className="form-actions">
                                {emailEditing && (
                                    <button type="button" className="steam-btn steam-btn-secondary" onClick={() => { setEmailEditing(false); setEmailInput(emailStatus.email || ''); }}>
                                        ยกเลิก
                                    </button>
                                )}
                                <button type="submit" className="steam-btn steam-btn-primary" disabled={emailSending}>
                                    {emailSending ? 'กำลังส่งรหัส...' : 'ส่งรหัสยืนยัน'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ฟอร์มกรอกรหัสยืนยัน */}
                    {codeSent && !emailStatus.email_verified && (
                        <form onSubmit={handleVerifyCode} className="email-verify-form">
                            <div className="email-verify-note">
                                เราได้ส่งรหัสยืนยัน 6 หลักไปที่ <strong>{emailStatus.email}</strong> แล้ว
                                (ตรวจในกล่องจดหมาย/สแปม) รหัสหมดอายุใน 15 นาที
                            </div>
                            <div className="email-verify-row">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="steam-input code-input"
                                    value={verifyCode}
                                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="______"
                                    maxLength="6"
                                />
                                <button type="submit" className="steam-btn steam-btn-primary" disabled={verifying}>
                                    {verifying ? 'กำลังยืนยัน...' : 'ยืนยัน'}
                                </button>
                            </div>
                            <button type="button" className="email-link-btn" onClick={handleSendCode} disabled={emailSending}>
                                ส่งรหัสใหม่อีกครั้ง
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* Security / 2FA */}
            <div className="steam-card mb-3">
                <div className="steam-card-header flex-between">
                    <span>ความปลอดภัย — ยืนยันตัวตน 2 ชั้น (2FA)</span>
                    {twofaEnabled
                        ? <span className="badge badge-success">เปิดใช้งาน</span>
                        : <span className="badge badge-secondary">ปิดอยู่</span>}
                </div>
                <div className="steam-card-body">
                    <p className="text-muted" style={{ marginBottom: 14, fontSize: 13 }}>
                        เพิ่มความปลอดภัยอีกชั้น เมื่อเข้าสู่ระบบจะต้องกรอกรหัส 6 หลักจากแอป
                        เช่น <strong>Google Authenticator</strong> หรือ <strong>Microsoft Authenticator</strong>
                    </p>

                    {/* เปิดอยู่แล้ว */}
                    {twofaEnabled && !twofaSetup && (
                        <div className="twofa-enabled-box">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#16a34a" strokeWidth="2">
                                <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
                            </svg>
                            <span>การยืนยันตัวตน 2 ชั้นเปิดใช้งานอยู่ บัญชีของคุณปลอดภัยขึ้น</span>
                            <button type="button" className="steam-btn steam-btn-danger" onClick={disable2fa} disabled={twofaBusy}>
                                ปิดใช้งาน
                            </button>
                        </div>
                    )}

                    {/* ปิดอยู่ และยังไม่เริ่ม setup */}
                    {!twofaEnabled && !twofaSetup && (
                        <button type="button" className="steam-btn steam-btn-primary" onClick={startTwofaSetup} disabled={twofaBusy}>
                            {twofaBusy ? 'กำลังเตรียม...' : 'เปิดใช้งาน 2FA'}
                        </button>
                    )}

                    {/* กำลัง setup — แสดง QR + key + วิธีตั้งค่า */}
                    {twofaSetup && (
                        <div className="twofa-setup">
                            <ol className="twofa-steps">
                                <li>
                                    เปิดแอป <strong>Google Authenticator</strong> หรือ <strong>Microsoft Authenticator</strong>
                                    บนมือถือ → กด <strong>+</strong> เพื่อเพิ่มบัญชีใหม่
                                </li>
                                <li>
                                    เลือก <strong>"สแกน QR code"</strong> แล้วสแกนภาพด้านล่าง
                                    <div className="twofa-qr">
                                        {twofaSetup.qr
                                            ? <img src={twofaSetup.qr} alt="QR code สำหรับ 2FA" />
                                            : <div className="twofa-qr-fallback">ไม่สามารถสร้าง QR ได้ — กรุณาใช้วิธีกรอกรหัสด้านล่าง</div>}
                                    </div>
                                </li>
                                <li>
                                    <strong>หรือ</strong> ถ้าสแกนไม่ได้ ให้เลือก <strong>"ใส่รหัสด้วยตนเอง / Enter a setup key"</strong>
                                    แล้วคัดลอกคีย์นี้ไปวาง:
                                    <div className="twofa-key-row">
                                        {showManualKey
                                            ? <code className="twofa-key">{twofaSetup.formattedKey}</code>
                                            : <button type="button" className="email-link-btn" onClick={() => setShowManualKey(true)}>แสดงคีย์สำหรับกรอกเอง</button>}
                                        {showManualKey && (
                                            <button type="button" className="twofa-copy-btn" onClick={copyKey}>
                                                {keyCopied ? 'คัดลอกแล้ว ✓' : 'คัดลอกคีย์'}
                                            </button>
                                        )}
                                    </div>
                                    {showManualKey && (
                                        <div className="twofa-manual-hint">
                                            ตั้งค่าในแอป: ประเภท <strong>Time based</strong>, ชื่อบัญชี <strong>iMoney</strong> —
                                            แล้วแอปจะสร้างรหัส 6 หลักที่เปลี่ยนทุก 30 วินาที
                                        </div>
                                    )}
                                </li>
                                <li>
                                    กรอกรหัส 6 หลักที่แสดงในแอปเพื่อยืนยัน:
                                    <form onSubmit={confirmEnable2fa} className="twofa-confirm">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="steam-input code-input"
                                            value={twofaCode}
                                            onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="______"
                                            maxLength="6"
                                        />
                                        <button type="submit" className="steam-btn steam-btn-primary" disabled={twofaBusy}>
                                            {twofaBusy ? 'กำลังยืนยัน...' : 'ยืนยันและเปิดใช้งาน'}
                                        </button>
                                        <button type="button" className="steam-btn steam-btn-secondary" onClick={() => { setTwofaSetup(null); setTwofaCode(''); }}>
                                            ยกเลิก
                                        </button>
                                    </form>
                                </li>
                            </ol>
                            <div className="twofa-warning">
                                ⚠️ เก็บคีย์/แอปไว้ให้ดี หากเปลี่ยนเครื่องหรือลบแอปโดยไม่ปิด 2FA ก่อน จะเข้าระบบไม่ได้
                                (ต้องติดต่อผู้ดูแลระบบเพื่อรีเซ็ต)
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Phone for financial transaction contact */}
            <div className="steam-card mb-3">
                <div className="steam-card-header flex-between">
                    <span>เบอร์โทรศัพท์สำหรับติดต่อ</span>
                    {phone && !phoneEditing && (
                        <span className="badge badge-success">บันทึกแล้ว</span>
                    )}
                </div>
                <div className="steam-card-body">
                    <p className="text-muted" style={{ marginBottom: 14, fontSize: 13 }}>
                        เพิ่มเบอร์โทรศัพท์เพื่อให้เจ้าหน้าที่การเงินติดต่อกลับเรื่องธุรกรรมทางการเงิน เช่น การโอนเงิน หรือการตรวจสอบบัญชี
                    </p>

                    {/* แสดงเบอร์ปัจจุบัน */}
                    {phone && !phoneEditing && (
                        <div className="phone-current">
                            <div className="phone-current-info">
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <span className="phone-number">{formatPhone(phone)}</span>
                            </div>
                            <div className="phone-current-actions">
                                <button type="button" className="email-link-btn" onClick={() => { setPhoneEditing(true); setPhoneInput(phone); }}>
                                    เปลี่ยนเบอร์
                                </button>
                                <button type="button" className="email-link-btn danger" onClick={handleRemovePhone}>
                                    ลบ
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ฟอร์มกรอก/เปลี่ยนเบอร์โทร */}
                    {(!phone || phoneEditing) && (
                        <form onSubmit={handleSavePhone} className="email-form">
                            <div className="form-group">
                                <label className="form-label">เบอร์โทรศัพท์</label>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    className="steam-input"
                                    value={phoneInput}
                                    onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d-\s]/g, '').slice(0, 12))}
                                    placeholder="เช่น 0812345678"
                                    required
                                />
                            </div>
                            <div className="form-actions">
                                {phoneEditing && (
                                    <button type="button" className="steam-btn steam-btn-secondary" onClick={() => { setPhoneEditing(false); setPhoneInput(phone || ''); }}>
                                        ยกเลิก
                                    </button>
                                )}
                                <button type="submit" className="steam-btn steam-btn-primary" disabled={phoneSaving}>
                                    {phoneSaving ? 'กำลังบันทึก...' : 'บันทึกเบอร์โทร'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Position change request */}
            <div className="steam-card mb-3">
                <div className="steam-card-header flex-between">
                    <span>ชื่อตำแหน่ง</span>
                    {positionData.request && positionData.request.status === 'pending' && (
                        <span className="badge badge-warning">รอตรวจสอบ</span>
                    )}
                </div>
                <div className="steam-card-body">
                    <div className="phone-current" style={{ marginBottom: 14 }}>
                        <div className="phone-current-info">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 7h-4V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM10 5h4v2h-4z" />
                            </svg>
                            <span className="phone-number">{currentPosition() || '-'}</span>
                        </div>
                    </div>

                    {/* ต้องยืนยันอีเมลก่อน */}
                    {!positionData.email_verified && (
                        <div className="email-verify-note" style={{ background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
                            หากต้องการขอแก้ไขชื่อตำแหน่ง กรุณา<strong>ยืนยันอีเมล</strong>ก่อน (ในหัวข้อด้านบน)
                            เพื่อให้ระบบใช้แจ้งผลการตรวจสอบกลับไปยังอีเมลของคุณ
                        </div>
                    )}

                    {/* คำขอที่รอดำเนินการ */}
                    {positionData.request && positionData.request.status === 'pending' ? (
                        <div className="email-verify-note">
                            ส่งคำขอแก้ไขชื่อตำแหน่งเป็น <strong>{positionData.request.requested_position}</strong> แล้ว
                            อยู่ระหว่างรอเจ้าหน้าที่การเงินตรวจสอบและยืนยัน เมื่อมีผลจะแจ้งไปทางอีเมล

                            {positionData.needsEvidence && (
                                <div style={{ marginTop: 12, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
                                    <p style={{ margin: '0 0 6px', color: '#92400e', fontWeight: 600 }}>📎 กรุณาแนบหลักฐานประกอบการพิจารณา</p>
                                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#78350f' }}>
                                        เช่น บัตรประจำตัวเจ้าหน้าที่ที่โรงพยาบาล/หน่วยงานราชการออกให้ ที่ระบุชื่อตำแหน่งชัดเจน
                                    </p>
                                    <ImageCapture
                                        onSelect={(file) => setPositionEvidence(file)}
                                        accept="image/*"
                                        captureLabel="ถ่ายรูปหลักฐาน"
                                        browseLabel="เลือกรูปจากเครื่อง"
                                    />
                                    {positionEvidence && (
                                        <small style={{ display: 'block', marginTop: 6, color: '#16a34a' }}>
                                            ✓ เลือกแล้ว: {positionEvidence.name || 'รูปที่ถ่าย'}
                                        </small>
                                    )}
                                    <div style={{ marginTop: 10 }}>
                                        <button type="button" className="steam-btn steam-btn-primary" onClick={handleUploadEvidence} disabled={positionSaving || !positionEvidence}>
                                            {positionSaving ? 'กำลังส่ง...' : 'ส่งหลักฐาน'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: 10 }}>
                                <button type="button" className="email-link-btn danger" onClick={handleCancelPositionRequest}>
                                    ยกเลิกคำขอ
                                </button>
                            </div>
                        </div>
                    ) : positionData.email_verified && (
                        <>
                            {/* ผลคำขอล่าสุด (ถ้ามีและไม่ใช่ pending) */}
                            {positionData.request && positionData.request.status === 'rejected' && (
                                <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
                                    คำขอล่าสุดถูกปฏิเสธ{positionData.request.admin_note ? ` — เหตุผล: ${positionData.request.admin_note}` : ''}
                                </p>
                            )}

                            {!positionFormOpen ? (
                                <button
                                    type="button"
                                    className="steam-btn steam-btn-secondary"
                                    onClick={() => { setPositionFormOpen(true); setPositionInput(currentPosition()); setPositionPhone(phone || ''); }}
                                >
                                    ขอแก้ไขชื่อตำแหน่ง
                                </button>
                            ) : (
                                <form onSubmit={handleSubmitPositionRequest} className="email-form">
                                    <div className="form-group">
                                        <label className="form-label">ชื่อตำแหน่งที่ถูกต้อง</label>
                                        <input
                                            type="text"
                                            className="steam-input"
                                            value={positionInput}
                                            onChange={(e) => setPositionInput(e.target.value)}
                                            placeholder="เช่น นักวิชาการคอมพิวเตอร์ชำนาญการ"
                                            maxLength={200}
                                            required
                                        />
                                        <small className="avatar-hint">
                                            คำขอจะถูกส่งให้เจ้าหน้าที่การเงินตรวจสอบและยืนยัน แล้วแจ้งผลกลับทางอีเมล
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">เบอร์โทรติดต่อกลับ (จำเป็น)</label>
                                        <input
                                            type="tel"
                                            className="steam-input"
                                            value={positionPhone}
                                            onChange={(e) => setPositionPhone(e.target.value)}
                                            placeholder="เช่น 0812345678"
                                            maxLength={15}
                                            required
                                        />
                                        <small className="avatar-hint">
                                            เผื่อเจ้าหน้าที่การเงินต้องโทรติดต่อยืนยันข้อมูลกับคุณ
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">แนบรูปหลักฐาน (จำเป็น)</label>
                                        <ImageCapture
                                            onSelect={(file) => setPositionEvidence(file)}
                                            accept="image/*"
                                            captureLabel="ถ่ายรูปหลักฐาน"
                                            browseLabel="เลือกรูปจากเครื่อง"
                                        />
                                        {positionEvidence && (
                                            <small style={{ display: 'block', marginTop: 6, color: '#16a34a' }}>
                                                ✓ เลือกแล้ว: {positionEvidence.name || 'รูปที่ถ่าย'}
                                            </small>
                                        )}
                                        <small className="avatar-hint">
                                            เช่น บัตรประจำตัวเจ้าหน้าที่ของรัฐ/โรงพยาบาล ที่ระบุชื่อตำแหน่งชัดเจน เพื่อประกอบการพิจารณา — รองรับ JPG/PNG/HEIC ไม่เกิน 8MB
                                        </small>
                                    </div>
                                    <div className="form-actions">
                                        <button type="button" className="steam-btn steam-btn-secondary" onClick={() => { setPositionFormOpen(false); setPositionInput(''); setPositionPhone(''); setPositionEvidence(null); }}>
                                            ยกเลิก
                                        </button>
                                        <button type="submit" className="steam-btn steam-btn-primary" disabled={positionSaving}>
                                            {positionSaving ? 'กำลังส่งคำขอ...' : 'ส่งคำขอ'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* My Signature */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <span>ลายเซ็นของฉัน</span>
                </div>
                <div className="steam-card-body">
                    {signatureLoading ? (
                        <div className="flex-center" style={{ padding: '40px 0' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : (
                        <div className="signature-section">
                            {signature ? (
                                <div className="signature-preview">
                                    <img
                                        src={`/api${signature.file_url}`}
                                        alt="ลายเซ็น"
                                        className="signature-image"
                                    />
                                    <div className="signature-actions">
                                        <button
                                            className="steam-btn steam-btn-primary"
                                            onClick={() => setShowSignaturePad(true)}
                                            disabled={uploadingSignature}
                                        >
                                            ✍️ เซ็นใหม่ด้วยนิ้ว
                                        </button>
                                        <label className="steam-btn steam-btn-secondary">
                                            อัปโหลดรูป
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/gif"
                                                onChange={handleSignatureUpload}
                                                ref={signatureInputRef}
                                                style={{ display: 'none' }}
                                                disabled={uploadingSignature}
                                            />
                                        </label>
                                        <button
                                            className="steam-btn steam-btn-danger"
                                            onClick={handleDeleteSignature}
                                            disabled={uploadingSignature}
                                        >
                                            ลบลายเซ็น
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="signature-upload">
                                    <div className="upload-placeholder">
                                        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <p>ยังไม่มีลายเซ็น</p>
                                        <small>เซ็นด้วยนิ้วบนมือถือได้เลย หรืออัปโหลดรูป (PNG, JPEG, GIF)</small>
                                    </div>
                                    <div className="signature-actions">
                                        <button
                                            className="steam-btn steam-btn-primary"
                                            onClick={() => setShowSignaturePad(true)}
                                            disabled={uploadingSignature}
                                        >
                                            ✍️ เซ็นด้วยนิ้ว
                                        </button>
                                        <label className="steam-btn steam-btn-secondary">
                                            อัปโหลดรูป
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/gif"
                                                onChange={handleSignatureUpload}
                                                ref={signatureInputRef}
                                                style={{ display: 'none' }}
                                                disabled={uploadingSignature}
                                            />
                                        </label>
                                    </div>
                                </div>
                            )}
                            <p className="signature-hint">
                                ลายเซ็นนี้จะถูกใช้ในเอกสารที่ Admin กำหนดให้คุณเป็นผู้ลงนาม — ระบบจะปรับขนาดและทำพื้นหลังโปร่งใสให้อัตโนมัติ
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {showSignaturePad && (
                <SignaturePad
                    onSave={handleSaveDrawnSignature}
                    onCancel={() => setShowSignaturePad(false)}
                    saving={uploadingSignature}
                />
            )}

            {/* Change Password */}
            <div className="steam-card">
                <div className="steam-card-header flex-between">
                    <span>เปลี่ยนรหัสผ่าน</span>
                    {!showPasswordForm && (
                        <button
                            className="steam-btn steam-btn-secondary"
                            onClick={() => setShowPasswordForm(true)}
                        >
                            เปลี่ยนรหัสผ่าน
                        </button>
                    )}
                </div>
                {showPasswordForm && (
                    <div className="steam-card-body">
                        <form onSubmit={handleChangePassword} className="password-form">
                            <div className="form-group">
                                <label className="form-label">รหัสผ่านปัจจุบัน</label>
                                <input
                                    type="password"
                                    className="steam-input"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">รหัสผ่านใหม่</label>
                                <input
                                    type="password"
                                    className="steam-input"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength="6"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">ยืนยันรหัสผ่านใหม่</label>
                                <input
                                    type="password"
                                    className="steam-input"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="steam-btn steam-btn-secondary"
                                    onClick={() => {
                                        setShowPasswordForm(false);
                                        setCurrentPassword('');
                                        setNewPassword('');
                                        setConfirmPassword('');
                                    }}
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    type="submit"
                                    className="steam-btn steam-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่านใหม่'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Profile;
